import { useRef, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FiChevronRight } from "react-icons/fi";
import { getLongestMatchingSubPath, pathMatchesRoute } from "./menuPathUtils";

const SubArrow = ({ active }) => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    className={`flex-shrink-0 transition-colors duration-200 ${
      active ? "text-white" : "text-white/30 group-hover/sub:text-white/55"
    }`}
  >
    <path
      d="M2 5h6M5.5 2.5L8 5l-2.5 2.5"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SidebarSubItem = ({ item, isActive }) => {
  const navigate = useNavigate();

  return (
    <button
      onClick={() => item.path && navigate(item.path)}
      className={`group/sub w-full flex items-center gap-2.5 pl-3 pr-2 py-[5px] rounded-lg transition-all duration-200 ${
        isActive
          ? "bg-white/[0.13] text-white"
          : "text-white/55 hover:text-white/90 hover:bg-white/[0.07]"
      }`}
    >
      <SubArrow active={isActive} />
      <span
        className={`text-[12.5px] truncate transition-all duration-200 ${
          isActive ? "font-semibold" : "font-medium"
        }`}
      >
        {item.label}
      </span>
      {isActive && (
        <span className="ml-auto w-1 h-1 rounded-full bg-white/60 flex-shrink-0" />
      )}
    </button>
  );
};

const SidebarMenuItem = ({
  item,
  isExpanded,
  openMenuLabel,
  onToggleMenu,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  const hasSubMenu = item.subMenu && item.subMenu.length > 0;
  const isOpen = openMenuLabel === item.label;

  const activeSubPath = hasSubMenu
    ? getLongestMatchingSubPath(item.subMenu, location.pathname)
    : null;

  const isActive = hasSubMenu
    ? Boolean(activeSubPath)
    : item.path
      ? item.path === "/"
        ? location.pathname === "/"
        : pathMatchesRoute(location.pathname, item.path)
      : false;

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [isOpen, item.subMenu, location.pathname, activeSubPath]);

  const handleClick = () => {
    if (hasSubMenu) {
      onToggleMenu(item.label);
    } else if (item.path) {
      navigate(item.path);
    }
  };

  return (
    <div className="relative mb-[1px]">
      <button
        onClick={handleClick}
        className={`relative w-full flex items-center gap-2.5 rounded-xl transition-all duration-200 ${
          isExpanded ? "px-2.5 py-[10px]" : "justify-center p-[7px]"
        } ${
          isActive
            ? "bg-white/[0.12] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
            : "text-white/60 hover:text-white/90 hover:bg-white/[0.07]"
        }`}
      >
        {/* Active left accent bar — inside the button so it centers on the button height only */}
        {isActive && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-white/70 rounded-r-full pointer-events-none" />
        )}
        {/* Icon */}
        <span
          className={`flex items-center justify-center flex-shrink-0 rounded-lg transition-all duration-200 ${
            isExpanded ? "w-7 h-7 text-[15px]" : "w-8 h-8 text-[16px]"
          } ${
            isActive
              ? "bg-white/[0.18] text-white shadow-sm"
              : "text-inherit opacity-80"
          }`}
        >
          {item.icon}
        </span>

        {isExpanded && (
          <>
            <span
              className={`flex-1 text-left text-[13px] truncate transition-all duration-200 ${
                isActive ? "font-semibold text-white" : "font-medium"
              }`}
            >
              {item.label}
            </span>
            {hasSubMenu && (
              <span
                className={`flex items-center justify-center transition-all duration-300 ${
                  isOpen ? "rotate-90" : ""
                }`}
              >
                <FiChevronRight className="w-3 h-3 opacity-40" />
              </span>
            )}
          </>
        )}
      </button>

      {/* Accordion submenu */}
      {hasSubMenu && isExpanded && (
        <div
          style={{
            maxHeight: isOpen ? `${contentHeight}px` : "0px",
            opacity: isOpen ? 1 : 0,
          }}
          className="overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
        >
          <div
            ref={contentRef}
            className="pt-1 pb-1 ml-[18px] border-l border-white/[0.1] pl-2 flex flex-col gap-[1px]"
          >
            {item.subMenu.map((subItem, idx) =>
              subItem.isGroupTitle ? (
                <div
                  key={idx}
                  className="pl-2 pr-3 pt-2 pb-1 text-[10px] font-semibold text-white/35 uppercase tracking-wider"
                >
                  {subItem.label}
                </div>
              ) : (
                <SidebarSubItem
                  key={idx}
                  item={subItem}
                  isActive={Boolean(
                    subItem.path &&
                      activeSubPath &&
                      subItem.path === activeSubPath
                  )}
                />
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SidebarMenuItem;
